import {
  bySensitiveDataCategory,
  SENSITIVE_DATA_CATEGORIES,
  type ConfigArea,
  type PermissionSetEntity,
  type SensitiveDataCategory,
} from '@omnea-digital/authz-model'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { type paths } from '@omnea-app/permissions/permissionsHttpClient/permissionsGeneratedTypes'
import { AUTHZ_FRESHNESS_MS } from './authzFreshness'
import { isNotFoundBody } from './isNotFoundBody'
import type { AuthzRollout } from './AuthzRolloutProvider'

export const GetAuthzContextQueryKey = 'GetAuthzContext'

/**
 * `unavailable` is a check that did not answer, which is not the same as `resolved` with empty sets:
 * empty sets are a denial, and callers may treat the two differently. It carries the error so a
 * caller can report the failure rather than present a check that will never complete as still running.
 *
 * `fgaAuthoritative` rides on the resolved answer because it decides how the answer is read: with it
 * off the service is asked only for the configuration surface, and every other rule keeps deciding by
 * role. That surface is itself a rollout: `canManagePermissions` reads false until RELEASE_FGA_CONFIG
 * names the caller, however the service answered.
 *
 * TODO(Permissions): remove the `role` state and every caller's fallback branch once the
 * permissions migration completes and no organisation answers 404.
 */
export type AuthzContextState =
  | { status: 'loading' | 'role' }
  | { status: 'unavailable'; error: unknown }
  | {
      status: 'resolved'
      fgaAuthoritative: boolean
      entities: ReadonlyMap<PermissionSetEntity, EntityAccess>
      requestAccessScope: AuthzContextBody['requestAccessScope']
      managedAreas: ReadonlySet<ConfigArea>
      canManagePermissions: boolean
      /** Only a system admin may make someone a super admin, so nobody promotes past themselves. */
      canPromoteToSuperAdmin: boolean
      canViewInsights: boolean
      canManageInsights: boolean
      canManageCustomFields: boolean
      canViewAllDocuments: boolean
      canEditAllDocuments: boolean
      canViewAllSubprocessors: boolean
      canViewRenewals: boolean
      /** Categories the service masks for the caller: their values come back null, and a filter or sort on them is refused. */
      hiddenSensitiveData: ReadonlySet<SensitiveDataCategory>
    }

export type EntityAccess = { canCreate: boolean }

export const LOADING_AUTHZ_CONTEXT: AuthzContextState = { status: 'loading' }

export const ROLE_AUTHZ_CONTEXT: AuthzContextState = { status: 'role' }

type AuthzContextBody = paths['/authz-context']['get']['responses'][200]['content']['application/json']

/** The 404 is cached as data rather than thrown, so the not-on-FGA answer survives a refetch. */
type AuthzContextResponse = { kind: 'authorized'; body: AuthzContextBody } | { kind: 'legacy' }

type ReadyAuthzRollout = Extract<AuthzRollout, { status: 'ready' }>

const toAuthzContextState = (response: AuthzContextResponse, rollout: ReadyAuthzRollout): AuthzContextState => {
  if (response.kind === 'legacy') return ROLE_AUTHZ_CONTEXT
  const {
    entities,
    requestAccessScope,
    configAreas,
    canManagePermissions,
    canPromoteToSuperAdmin,
    canViewInsights,
    canManageInsights,
    canManageCustomFields,
    canViewAllDocuments,
    canEditAllDocuments,
    canViewAllSubprocessors,
    canViewRenewals,
    // A service deployed before the field, or before a category, masks nothing.
    sensitiveData = bySensitiveDataCategory(() => true),
  } = response.body
  return {
    status: 'resolved',
    fgaAuthoritative: rollout.fgaAuthoritative,
    entities: new Map(entities.map(({ kind, canCreate }) => [kind, { canCreate }])),
    requestAccessScope,
    managedAreas: new Set(configAreas.filter(({ canManage }) => canManage).map(({ area }) => area)),
    canManagePermissions: rollout.permissionsConfigAvailable && canManagePermissions,
    canPromoteToSuperAdmin,
    canViewInsights,
    canManageInsights,
    canManageCustomFields,
    canViewAllDocuments,
    canEditAllDocuments,
    canViewAllSubprocessors,
    canViewRenewals,
    hiddenSensitiveData: new Set(SENSITIVE_DATA_CATEGORIES.filter((category) => !(sensitiveData[category] ?? true))),
  }
}

/**
 * What the current user has access to, resolved in one call at boot: the entities they have
 * access to and may create records of, the config areas they manage, whether they may administer
 * permissions, and whether the insights section opens for them.
 *
 * An entity is present on entitlement, not on there being records to show — a grant conferring nothing
 * today still means access, since the caller may create the first record.
 *
 * Access is conveyed by presence, so an absent entity is a denial and empty sets are a real answer.
 */
export const useAuthzContextQuery = (rollout: AuthzRollout): AuthzContextState => {
  const { getToken, user } = useAuthenticatedUser()
  // The service is asked when either rollout switch is on: enforcement needs the entities and config
  // areas, the configuration surface needs canManagePermissions. Neither on is an organisation the
  // service does not answer for, and nothing is fetched.
  const enabled = rollout.status === 'ready' && (rollout.fgaAuthoritative || rollout.permissionsConfigAvailable)
  // react-query reuses a select result only while the function keeps its reference. It also runs the
  // select on cached data whatever `enabled` says, so an unresolved rollout maps to loading here too.
  const select = useCallback(
    (response: AuthzContextResponse) =>
      rollout.status === 'ready' ? toAuthzContextState(response, rollout) : LOADING_AUTHZ_CONTEXT,
    [rollout]
  )
  const { data, isError, error } = useQuery({
    queryKey: [GetAuthzContextQueryKey, user.id],
    queryFn: async (): Promise<AuthzContextResponse> => {
      const authorization = await getToken()
      const permissionsClient = new PermissionsHttpClient({ authorization })
      const { data: response, error } = await permissionsClient.GET('/authz-context', {})
      if (isNotFoundBody(error)) return { kind: 'legacy' }
      throwIfApiError(error)
      if (response == null) throw new Error('authz context request returned no body')
      return { kind: 'authorized', body: response }
    },
    select,
    enabled,
    staleTime: AUTHZ_FRESHNESS_MS,
  })

  // Stable per error, so consumers that memoise on the context do not recompute every render.
  const unavailable = useMemo((): AuthzContextState => ({ status: 'unavailable', error }), [error])

  if (rollout.status === 'pending') return LOADING_AUTHZ_CONTEXT
  if (!enabled) return ROLE_AUTHZ_CONTEXT
  // A failed background refetch keeps the last-known answer rather than pulling sections mid-session.
  if (data != null) return data
  if (isError) return unavailable
  return LOADING_AUTHZ_CONTEXT
}
